import InfoIcon from '@mui/icons-material/Info'
import Stack from '@mui/material/Stack'
import { useMutation } from '@tanstack/react-query'
import KNCaption from 'components/KNCaption/KNCaption'
import KNFormDropzone from 'components/KNForm/KNFormDropzone'
import { type ReactElement } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { maxFileSize, minFileSize, uploadFile, type UploadFileResponse } from './StatusManager.service'

interface DocumentsUploadFieldsProps {
  stopType: 'PUP' | 'DEL' | 'CUS'
  showProof?: boolean
  hasProof?: boolean
  showPhotos?: boolean
  showPalletsDocument?: boolean
  hasPalletsDocument?: boolean
  showTro?: boolean
  hasTro?: boolean
  onPending?: (pending: boolean) => void
  weblinkToken?: string
}

const DocumentsUploadFields = ({
  stopType,
  showProof,
  hasProof,
  showPhotos,
  showPalletsDocument,
  hasPalletsDocument,
  showTro,
  hasTro,
  onPending,
  weblinkToken,
}: DocumentsUploadFieldsProps): ReactElement => {
  const { t } = useTranslation()
  const { control, setValue, getValues, setError, clearErrors } = useFormContext()
  const uploadFileMutation = useMutation({
    mutationFn: (file: File) => uploadFile(file, weblinkToken),
  })

  const processFile = async (file: File): Promise<UploadFileResponse> => {
    const uploadedFile = await uploadFileMutation.mutateAsync(file)
    // NOTE: if the flag is missing, assume it's a proper document so it won't display a warning message
    uploadedFile.isDocument ??= true
    return uploadedFile
  }

  return (
    <Stack spacing={2}>
      {showProof && ['PUP', 'DEL'].includes(stopType) && (
        <Stack spacing={0.5}>
          <KNFormDropzone
            label={
              stopType === 'PUP'
                ? t('screens.cs.trip_details.upload_document.attach_pop')
                : t('screens.cs.trip_details.upload_document.attach_pod')
            }
            name={stopType === 'PUP' ? 'popFiles' : 'podFiles'}
            control={control}
            setError={setError}
            clearErrors={clearErrors}
            setValue={setValue}
            disabled={hasProof ?? getValues('paperless')}
            accept={['image/jpeg', 'image/png', 'application/pdf']}
            size="small"
            multiple
            processFile={processFile}
            minFileSize={minFileSize}
            maxFileSize={maxFileSize}
            validateDocuments
            onPending={onPending}
          />
          {hasProof && (
            <KNCaption icon={<InfoIcon />}>
              {t('screens.cs.trip_details.upload_document.proof_already_attached')}
            </KNCaption>
          )}
          {getValues('paperless') && (
            <KNCaption icon={<InfoIcon />}>
              {t('screens.cs.trip_details.upload_document.proof_disabled_for_paperless')}
            </KNCaption>
          )}
        </Stack>
      )}
      {showPhotos && (
        <KNFormDropzone
          label={t('screens.cs.trip_details.upload_document.attach_photos')}
          name="photoFiles"
          control={control}
          setError={setError}
          clearErrors={clearErrors}
          setValue={setValue}
          accept={['image/jpeg', 'image/png', 'application/pdf']}
          size="small"
          multiple
          processFile={processFile}
          minFileSize={minFileSize}
          maxFileSize={maxFileSize}
          onPending={onPending}
        />
      )}
      {showPalletsDocument && (
        <Stack spacing={0.5}>
          <KNFormDropzone
            label={t('screens.cs.trip_details.upload_document.attach_pallet_slip')}
            name="palletFile"
            control={control}
            setError={setError}
            clearErrors={clearErrors}
            setValue={setValue}
            disabled={hasPalletsDocument}
            accept={['image/jpeg', 'image/png', 'application/pdf']}
            size="small"
            processFile={processFile}
            minFileSize={minFileSize}
            maxFileSize={maxFileSize}
            validateDocuments
            onPending={onPending}
          />
          {hasPalletsDocument && (
            <KNCaption icon={<InfoIcon />}>
              {t('screens.cs.trip_details.upload_document.pallet_slip_already_attached')}
            </KNCaption>
          )}
        </Stack>
      )}
      {showTro && stopType === 'DEL' && (
        <Stack spacing={0.5}>
          <KNFormDropzone
            label={t('screens.cs.trip_details.upload_document.attach_temperature_read_out')}
            name="troFile"
            control={control}
            setError={setError}
            clearErrors={clearErrors}
            setValue={setValue}
            disabled={hasTro}
            accept={['image/jpeg', 'image/png', 'application/pdf']}
            size="small"
            processFile={processFile}
            minFileSize={minFileSize}
            maxFileSize={maxFileSize}
            validateDocuments
            onPending={onPending}
          />
          {hasTro && (
            <KNCaption icon={<InfoIcon />}>
              {t('screens.cs.trip_details.upload_document.temperature_read_out_already_attached')}
            </KNCaption>
          )}
        </Stack>
      )}
    </Stack>
  )
}

export default DocumentsUploadFields
